/**
 * URL validation utilities to prevent open redirect vulnerabilities
 */

/**
 * Validates if a URL is safe to open
 * @param url The URL to validate
 * @returns true if URL is safe, false otherwise
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const parsed = new URL(url);

    // Only allow http and https protocols
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return false;
    }

    // Check for common attack patterns
    if (
      url.includes("javascript:") ||
      url.includes("data:") ||
      url.includes("vbscript:") ||
      url.includes("file:")
    ) {
      return false;
    }

    // URL is valid
    return true;
  } catch {
    // If URL parsing fails, check if it's a relative URL
    // Relative URLs are considered safe for internal navigation
    return url.startsWith("/") && !url.startsWith("//");
  }
}

/**
 * Produces a comparison key for a URL, ignoring scheme, "www.", trailing
 * slashes and case. Used to decide whether two links are "the same" when
 * deduping imports and guest merges.
 */
export function normalizeUrlKey(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    const path = parsed.pathname.replace(/\/+$/, "");
    return `${host}${path}${parsed.search}`;
  } catch {
    return url.trim().toLowerCase();
  }
}

/**
 * Sanitizes a URL for safe usage
 * @param url The URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
  if (!isValidUrl(url)) {
    console.warn("Invalid URL detected:", url);
    return "";
  }

  // Additional sanitization if needed
  return url.trim();
}

/**
 * Opens a URL in a new tab safely
 * @param url The URL to open
 * @returns true if opened successfully, false otherwise
 */
export function safeOpenUrl(url: string): boolean {
  const sanitized = sanitizeUrl(url);

  if (!sanitized) {
    console.error("Attempted to open invalid URL:", url);
    return false;
  }

  window.open(sanitized, "_blank", "noopener,noreferrer");
  return true;
}
